import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getUsers, deleteUser, updateUserStatus, type User } from "../../services/users";
import { getClubById, type Club } from "../../services/clubs";
import LoginAsButton from "../../components/LoginAsButton";
import Pagination, { usePagination } from "../../components/Pagination";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import { formatDate } from "../../lib/dateFormat";

type ClubAdminRow = {
  user: User;
  club?: Club | null;
  coachCount: number;
  athleteCount: number;
};

type UserStatus = "active" | "demo" | "inactive";

export default function ClubAdmins() {
  const navigate = useNavigate();
  const [clubAdmins, setClubAdmins] = useState<ClubAdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [clubFilter, setClubFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState<string | null>(null);
  // Which row's status pill has its picker open (click the pill to change).
  const [statusMenuFor, setStatusMenuFor] = useState<string | null>(null);

  const STATUS_OPTIONS: { value: UserStatus; label: string }[] = [
    { value: "active", label: "Activ" },
    { value: "demo", label: "Demo" },
    { value: "inactive", label: "Inactiv" },
  ];

  useEffect(() => {
    fetchClubAdmins();
  }, []);

  const fetchClubAdmins = async () => {
    try {
      setLoading(true);
      // pageSize=500 — see Athletes.tsx comment; the default 20
      // truncated big rosters and any client-side filter would come
      // up short.
      const response = await getUsers({ role: "club_admin", pageSize: 500 });

      // Fetch club details and counts for each club admin
      const clubAdminsWithDetails = await Promise.all(
        response.data.map(async (user) => {
          let club: Club | null = null;
          let coachCount = 0;
          let athleteCount = 0;

          // Get club ID from user roles
          const clubRole = user.roles?.find(r => r.code === "club_admin" && r.scope_type === "club");

          if (clubRole?.scope_id) {
            try {
              club = await getClubById(clubRole.scope_id);

              // Fetch coaches count for this club
              const coachesResponse = await getUsers({
                role: "coach",
                clubId: clubRole.scope_id
              });
              coachCount = coachesResponse.data.length;

              // Fetch athletes count for this club
              const athletesResponse = await getUsers({
                role: "athlete",
                clubId: clubRole.scope_id
              });
              athleteCount = athletesResponse.data.length;
            } catch (error) {
              console.error("Failed to fetch club details:", error);
            }
          }

          return {
            user,
            club,
            coachCount,
            athleteCount,
          };
        })
      );

      setClubAdmins(clubAdminsWithDetails);
    } catch (error) {
      console.error("Failed to fetch club admins:", error);
      toast.error("Nu s-au putut încărca administratorii de club");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (userId: string) => {
    if (deleteConfirmId === userId) {
      // Second click - proceed with delete
      handleDeleteConfirm(userId);
    } else {
      // First click - show confirmation
      setDeleteConfirmId(userId);
      setTimeout(() => setDeleteConfirmId(null), 3000); // Reset after 3 seconds
    }
  };

  const handleDeleteConfirm = async (userId: string) => {
    try {
      await deleteUser(userId);
      toast.success("Admin club șters cu succes!");
      setDeleteConfirmId(null);
      fetchClubAdmins();
    } catch (error: any) {
      console.error("Failed to delete club admin:", error);
      toast.error(error.response?.data?.error || "Nu s-a putut șterge administratorul de club");
    }
  };

  const handleStatusChange = async (userId: string, newStatus: UserStatus) => {
    setStatusLoading(userId);
    try {
      const result = await updateUserStatus(userId, newStatus, true);
      toast.success(
        `Status schimbat în "${newStatus}"${result.cascadeCount > 0 ? ` (+ ${result.cascadeCount} membri actualizați)` : ""}`
      );
      fetchClubAdmins();
    } catch (error: any) {
      console.error("Failed to update status:", error);
      toast.error(error.response?.data?.error || "Eroare la schimbarea statusului");
    } finally {
      setStatusLoading(null);
    }
  };

  const getStatusBadge = (status?: string, demoExpiresAt?: string | null, demoStartedAt?: string | null) => {
    if (status === "active") {
      return (
        <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-semibold cursor-default">
          Activ
        </span>
      );
    }
    if (status === "demo") {
      const daysLeft = demoExpiresAt
        ? Math.max(0, Math.ceil((new Date(demoExpiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
        : 0;
      const tooltipText = demoStartedAt && demoExpiresAt
        ? `Demo început: ${formatDate(new Date(demoStartedAt))}\nExpiră: ${formatDate(new Date(demoExpiresAt))}\nTimp rămas: ${daysLeft} zile`
        : demoExpiresAt
        ? `Expiră: ${formatDate(new Date(demoExpiresAt))}\nTimp rămas: ${daysLeft} zile`
        : `Timp rămas: ${daysLeft} zile`;
      return (
        <span
          className={`px-2.5 py-1 rounded-lg text-xs font-semibold cursor-help ${daysLeft <= 3 ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"}`}
          title={tooltipText}
        >
          Demo ({daysLeft} zile)
        </span>
      );
    }
    if (status === "inactive") {
      return (
        <span className="px-2.5 py-1 bg-red-50 text-red-600 rounded-lg text-xs font-semibold cursor-default">
          Inactiv
        </span>
      );
    }
    return (
      <span className="px-2.5 py-1 bg-slate-100 text-slate-500 rounded-lg text-xs font-semibold cursor-default">
        -
      </span>
    );
  };

  /**
   * The CLUB's state, which is not the admin's.
   *
   * These two were being read off one pill and they answer different
   * questions: the account pill says whether this person can sign in, the club
   * pill says whether the club is paying and whether its people can still
   * write. A club admin can perfectly well be an active account on a club
   * whose trial expired last week, and squashing that into one badge hid
   * exactly the case worth seeing.
   */
  const getClubStatusBadge = (club?: Club | null) => {
    const pill = (cls: string, label: string, title?: string) => (
      <span
        className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${cls} ${title ? "cursor-help" : "cursor-default"}`}
        title={title}
      >
        {label}
      </span>
    );

    if (!club) return pill("bg-slate-100 text-slate-500", "-");

    if (club.status && club.status !== "active") {
      return pill("bg-red-50 text-red-600", club.status === "suspended" ? "Suspendat" : club.status);
    }

    const plan = club.plan_code || "demo";
    if (plan === "basic") return pill("bg-emerald-50 text-emerald-700", "Baza");
    if (plan === "premium") return pill("bg-violet-50 text-violet-700", "Premium");

    if (!club.trial_ends_at) {
      return pill("bg-slate-100 text-slate-500", "Demo", "Fără dată de expirare — clubul nu se blochează.");
    }

    const ends = new Date(club.trial_ends_at);
    const daysLeft = Math.ceil((ends.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 0) {
      return pill("bg-red-50 text-red-600", "Demo expirat", `A expirat pe ${formatDate(ends)} — clubul e doar în citire.`);
    }
    return pill(
      daysLeft <= 3 ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700",
      `Demo (${daysLeft} ${daysLeft === 1 ? "zi" : "zile"})`,
      `Expiră pe ${formatDate(ends)}`
    );
  };

  // Filter options derived from the club admin rows themselves —
  // there's no separate /clubs fetch here since each row already
  // carries its club (fetched per-admin by getClubById above). Rows
  // without a club can't be filtered by club/city and are dropped
  // whenever either filter is set.
  const clubOptions = useMemo(
    () =>
      Array.from(
        new Map(
          clubAdmins
            .filter((r): r is ClubAdminRow & { club: Club } => !!r.club)
            .map((r) => [r.club.id, r.club])
        ).values()
      ).sort((a, b) => a.name.localeCompare(b.name)),
    [clubAdmins]
  );
  const cityOptions = useMemo(
    () =>
      Array.from(
        new Set(
          clubAdmins
            .map((r) => r.club?.city)
            .filter((v): v is string => !!v)
        )
      ).sort(),
    [clubAdmins]
  );

  const filteredClubAdmins = clubAdmins.filter((adminRow) => {
    const searchLower = searchTerm.toLowerCase();
    const searchMatch =
      adminRow.user.full_name?.toLowerCase().includes(searchLower) ||
      adminRow.user.email.toLowerCase().includes(searchLower) ||
      adminRow.club?.name.toLowerCase().includes(searchLower);
    if (!searchMatch) return false;

    if (clubFilter && adminRow.club?.id !== clubFilter) return false;
    if (cityFilter && adminRow.club?.city !== cityFilter) return false;
    return true;
  });

  // Lista vine întreagă de la server (pageSize=500), deci paginăm în client.
  const { page, setPage, pageSize, total, visible: pagedClubAdmins } = usePagination(filteredClubAdmins);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-slate-600 font-medium">Se încarcă...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-[1500px]">
        {/* Header */}
        <AdminPageHeader
          eyebrow="Utilizatori și membri"
          title="Administratori club"
          subtitle={<>Gestionare administratori club ({filteredClubAdmins.length})</>}
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M16 3.13a4 4 0 010 7.75M8 3.13a4 4 0 000 7.75" />
            </svg>
          }
          actions={
            <AdminButton
              onClick={() => navigate("/admin/users")}
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
            >
              Adaugă Admin Club Nou
            </AdminButton>
          }
        />

        {/* Search + filters */}
        <div className="mb-6 bg-white rounded-2xl shadow-sm border border-slate-200 p-4 space-y-3">
          <div className="relative">
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Caută admin club după nume, email sau club..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select
              value={clubFilter}
              onChange={(e) => setClubFilter(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Toate cluburile</option>
              {clubOptions.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Toate orașele</option>
              {cityOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          {(clubFilter || cityFilter) && (
            <button
              onClick={() => { setClubFilter(""); setCityFilter(""); }}
              className="text-xs text-slate-500 hover:text-slate-700 underline"
            >
              Șterge filtrele
            </button>
          )}
        </div>

        {/* Club Admins Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Nume
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Status cont
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Status club
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Antrenori
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Sportivi
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Creat
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Acțiuni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredClubAdmins.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                      <div className="flex flex-col items-center gap-2">
                        <svg className="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span>Nu există administratori de club înregistrați</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  pagedClubAdmins.map((adminRow) => (
                    <tr
                      key={adminRow.user.id}
                      className="hover:bg-slate-50 transition-colors duration-150"
                    >
                      {/* Name - Clickable, with club + city underneath */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/admin/users/${adminRow.user.id}`)}
                          className="block text-indigo-600 hover:text-indigo-700 font-medium hover:underline transition-colors"
                        >
                          {adminRow.user.full_name || adminRow.user.email}
                        </button>
                        {(adminRow.club?.name || adminRow.club?.city) && (
                          <div className="text-xs text-slate-400 mt-0.5">
                            {[adminRow.club?.name, adminRow.club?.city].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </td>

                      {/* Account status — click the pill to change it */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="relative inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setStatusMenuFor(statusMenuFor === adminRow.user.id ? null : adminRow.user.id)}
                            disabled={statusLoading === adminRow.user.id}
                            className="inline-flex items-center gap-1 rounded-full disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-1"
                            title="Schimbă statusul"
                          >
                            {getStatusBadge(adminRow.user.status, adminRow.user.demo_expires_at, adminRow.user.demo_started_at)}
                            <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {statusLoading === adminRow.user.id && (
                            <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                          )}
                          {statusMenuFor === adminRow.user.id && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setStatusMenuFor(null)} />
                              <div className="absolute left-0 top-full mt-1 z-20 w-36 bg-white border border-slate-200 rounded-lg shadow-lg py-1">
                                {STATUS_OPTIONS.map((opt) => {
                                  const current = (adminRow.user.status || "active") === opt.value;
                                  return (
                                    <button
                                      key={opt.value}
                                      onClick={() => { setStatusMenuFor(null); if (!current) handleStatusChange(adminRow.user.id, opt.value); }}
                                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-slate-50 ${current ? "text-indigo-700 font-medium" : "text-slate-700"}`}
                                    >
                                      <span className={`w-1.5 h-1.5 rounded-full ${opt.value === "active" ? "bg-emerald-500" : opt.value === "demo" ? "bg-indigo-500" : "bg-rose-500"}`} />
                                      {opt.label}
                                      {current && (
                                        <svg className="w-3.5 h-3.5 ml-auto text-indigo-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" /></svg>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                      </td>

                      {/* Club status — the club's plan and trial, not the person's account */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {adminRow.club ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/admin/clubs/${adminRow.club!.id}`)}
                            className="focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-1 rounded-lg"
                            title="Deschide clubul"
                          >
                            {getClubStatusBadge(adminRow.club)}
                          </button>
                        ) : (
                          getClubStatusBadge(null)
                        )}
                      </td>

                      {/* Coach Count */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-semibold">
                          {adminRow.coachCount}
                        </span>
                      </td>

                      {/* Athlete Count */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-semibold">
                          {adminRow.athleteCount}
                        </span>
                      </td>

                      {/* Created At */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-slate-500 text-sm">
                          {formatDate(new Date(adminRow.user.created_at))}
                        </span>
                      </td>

                      {/* Actions - Login-as + Delete */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <LoginAsButton userId={adminRow.user.id} compact />
                          <button
                            onClick={() => handleDeleteClick(adminRow.user.id)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                              deleteConfirmId === adminRow.user.id
                                ? "bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-500/25"
                                : "bg-red-50 text-red-600 hover:bg-red-100"
                            }`}
                          >
                            {deleteConfirmId === adminRow.user.id ? "Confirmă Ștergere" : "Șterge"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
        </div>
      </div>
    </div>
  );
}
